import { Button, Group, Modal, MultiSelect, Stack, Text, TextInput, rem } from '@mantine/core';
import {
  bboxIntersects,
  bboxOf,
  formatKm,
  MAX_GPX_BYTES,
  padBbox,
  parseGpxDocument,
  type Bbox,
  type ParsedGpx,
  type Position,
} from '@reditelji/shared';
import { useEffect, useMemo, useRef, useState } from 'react';
import { CircleMarker, MapContainer, Polyline, TileLayer, useMap } from 'react-leaflet';
import { useEvent, useRouteMutations } from '../api/hooks';
import type { EventDay, RaceRoute, TimelinePost } from '../api/types';
import { plT } from '../lib/ui';

/**
 * Colours offered to a new course. The
 * picker skips whatever is already taken so two courses never share a line
 * colour on the map.
 */
export const ROUTE_PALETTE = [
  'oklch(0.55 0.14 25)',
  'oklch(0.5 0.12 245)',
  'oklch(0.58 0.11 90)',
  'oklch(0.52 0.12 155)',
  'oklch(0.55 0.13 305)',
  'oklch(0.5 0.1 195)',
];

/** How far outside the posts' bounding box a track may still sit, in degrees. */
const AREA_TOLERANCE_DEG = 0.5;

/** Row ids only need to be unique within one open modal. */
let rowSeq = 0;

interface Row {
  key: string;
  fileName: string;
  name: string;
  color: string;
  eventDayIds: string[];
  gpx: string;
  parsed: ParsedGpx | null;
  parseError: string | null;
  /** Set once the row has been sent. */
  result: 'ok' | { error: string } | null;
}

/** Filename minus its extension — the fallback when the file carries no name. */
function stemOf(fileName: string): string {
  return fileName.replace(/\.gpx$/i, '').replace(/[_-]+/g, ' ').trim();
}

export function RouteImportModal({
  opened,
  onClose,
  routes,
  posts,
  replacing,
  onImported,
}: {
  opened: boolean;
  onClose: () => void;
  routes: RaceRoute[];
  posts: TimelinePost[];
  /** Opened from a route's "Zamenjaj z GPX…" — the first file replaces it. */
  replacing: RaceRoute | null;
  onImported: (bbox: Bbox | null) => void;
}) {
  const event = useEvent();
  const mut = useRouteMutations();
  const [rows, setRows] = useState<Row[]>([]);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const days: EventDay[] = event.data?.days ?? [];

  // The event's footprint, for the "is this the right race?" check.
  const postArea = useMemo<Bbox | null>(() => {
    const located = posts
      .filter((p) => p.lat !== null && p.lng !== null)
      .map((p): Position => [p.lng!, p.lat!]);
    const bbox = located.length ? bboxOf([located]) : null;
    return bbox ? padBbox(bbox, AREA_TOLERANCE_DEG) : null;
  }, [posts]);

  useEffect(() => {
    if (!opened) {
      setRows([]);
      setBusy(false);
    }
  }, [opened]);

  async function addFiles(files: File[]) {
    const added: Row[] = [];
    for (const file of files) {
      // A monotonic id: the same file dropped in two batches must not collide,
      // or patchRow would edit both rows at once.
      const key = `row-${(rowSeq += 1)}`;
      if (file.size > MAX_GPX_BYTES) {
        added.push({
          key,
          fileName: file.name,
          name: stemOf(file.name),
          color: ROUTE_PALETTE[0]!,
          eventDayIds: [],
          gpx: '',
          parsed: null,
          parseError: `Datoteka je prevelika (${(file.size / 1024 / 1024).toFixed(1)} MB, največ ${Math.round(MAX_GPX_BYTES / 1_000_000)} MB).`,
          result: null,
        });
        continue;
      }
      const gpx = await file.text();
      let parsed: ParsedGpx | null = null;
      let parseError: string | null = null;
      try {
        // The same reader the API uses, so the preview matches what gets stored.
        parsed = parseGpxDocument(new DOMParser().parseFromString(gpx, 'text/xml'));
      } catch (err) {
        parseError = err instanceof Error ? err.message : 'Datoteke ni bilo mogoče prebrati.';
      }
      added.push({
        key,
        fileName: file.name,
        name: parsed?.name ?? stemOf(file.name),
        color: ROUTE_PALETTE[0]!,
        eventDayIds: [],
        gpx,
        parsed,
        parseError,
        result: null,
      });
    }
    // Assign distinct colours only once the whole batch is known.
    setRows((prev) => {
      const taken = new Set([...routes.map((r) => r.color), ...prev.map((r) => r.color)]);
      const coloured = added.map((row, i) => {
        // Opened from a course's "Zamenjaj z GPX…": the first file takes that
        // course's identity, so the upsert replaces it instead of adding a
        // second route under whatever name the file happened to carry.
        const target =
          (replacing && !prev.length && i === 0 ? replacing : null) ??
          routes.find((r) => r.name === row.name);
        // A file replacing a known course inherits its look, so a re-import
        // cannot silently recolour it or move it in the list.
        if (target)
          return {
            ...row,
            name: target.name,
            color: target.color,
            eventDayIds: target.eventDayIds,
          };
        const colour = ROUTE_PALETTE.find((c) => !taken.has(c)) ?? ROUTE_PALETTE[0]!;
        taken.add(colour);
        return { ...row, color: colour };
      });
      return [...prev, ...coloured];
    });
  }

  const patchRow = (key: string, patch: Partial<Row>) =>
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  // Failed rows are retried by runImport, so they still count as pending here;
  // otherwise a batch that all failed would leave the button permanently off.
  const ready = rows.filter((r) => r.parsed && r.result !== 'ok');

  async function runImport() {
    setBusy(true);
    // Collected here rather than read back off `rows`, which stays the
    // render-time snapshot for the whole loop.
    const stored: Position[][] = [];
    for (const row of rows) {
      if (!row.parsed || row.result === 'ok') continue;
      try {
        await mut.importGpx.mutateAsync({
          name: row.name.trim(),
          color: row.color,
          eventDayIds: row.eventDayIds,
          sourceName: row.fileName,
          gpx: row.gpx,
        });
        patchRow(row.key, { result: 'ok' });
        stored.push(...row.parsed.lines);
      } catch (err) {
        patchRow(row.key, {
          result: { error: err instanceof Error ? err.message : 'Uvoz ni uspel.' },
        });
      }
    }
    setBusy(false);
    // Fit to what was stored, not to what was merely parsed.
    onImported(stored.length ? bboxOf(stored) : null);
  }

  const allDone = rows.length > 0 && rows.every((r) => r.result === 'ok');

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={replacing ? `Zamenjaj traso »${replacing.name}«` : 'Uvoz tras (GPX)'}
      size="xl"
      zIndex={1400}
    >
      <Stack gap={10}>
        <Text size="xs" c="dimmed">
          {replacing
            ? `Izbrana datoteka bo nadomestila geometrijo trase »${replacing.name}«; ime, barva in dnevi ostanejo. Sled se najprej nariše za predogled — v bazo se zapiše šele ob potrditvi.`
            : 'Izberi datoteke GPX s progami (ena datoteka na progo). Sled se najprej nariše za predogled — v bazo se zapiše šele ob potrditvi. Ponoven uvoz trase z istim imenom zamenja njeno geometrijo.'}
        </Text>

        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            void addFiles(Array.from(e.dataTransfer.files));
          }}
          onClick={() => inputRef.current?.click()}
          style={{
            border: `1.5px dashed ${dragging ? 'oklch(0.62 0.09 245)' : 'oklch(0.88 0.006 95)'}`,
            background: dragging ? 'oklch(0.97 0.02 245)' : 'oklch(0.985 0.004 95)',
            borderRadius: 10,
            padding: '18px 14px',
            textAlign: 'center',
            cursor: 'pointer',
          }}
        >
          <Text size="sm" fw={600}>
            Povleci datoteke .gpx sem
          </Text>
          <Text size="xs" c="dimmed">
            ali klikni za izbiro
          </Text>
          <input
            ref={inputRef}
            type="file"
            accept=".gpx,application/gpx+xml"
            multiple
            hidden
            onChange={(e) => {
              void addFiles(Array.from(e.currentTarget.files ?? []));
              e.currentTarget.value = '';
            }}
          />
        </div>

        {rows.map((row) => (
          <RowCard
            key={row.key}
            row={row}
            days={days}
            routes={routes}
            postArea={postArea}
            onPatch={(patch) => patchRow(row.key, patch)}
            onRemove={() => setRows((prev) => prev.filter((r) => r.key !== row.key))}
          />
        ))}

        {rows.some((r) => r.parsed) ? <PreviewMap rows={rows} posts={posts} /> : null}

        <Group justify="flex-end" gap={8}>
          <Button size="compact-sm" variant="default" onClick={onClose}>
            {allDone ? 'Zapri' : 'Prekliči'}
          </Button>
          <Button
            size="compact-sm"
            loading={busy}
            disabled={!ready.length || ready.some((r) => !r.name.trim())}
            onClick={() => void runImport()}
          >
            Uvozi ({ready.length})
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function RowCard({
  row,
  days,
  routes,
  postArea,
  onPatch,
  onRemove,
}: {
  row: Row;
  days: EventDay[];
  routes: RaceRoute[];
  postArea: Bbox | null;
  onPatch: (patch: Partial<Row>) => void;
  onRemove: () => void;
}) {
  const existing = routes.find((r) => r.name.trim() === row.name.trim());
  const outsideArea =
    row.parsed?.bbox && postArea ? !bboxIntersects(row.parsed.bbox, postArea) : false;

  return (
    <div
      style={{
        border: '1px solid oklch(0.92 0.006 95)',
        borderRadius: 10,
        padding: '10px 12px',
        background: row.parseError ? 'oklch(0.99 0.015 25)' : 'oklch(1 0 0)',
      }}
    >
      <Group gap={8} align="flex-start" wrap="nowrap">
        <div style={{ flex: 1, minWidth: 0 }}>
          <Group gap={6} mb={6} wrap="nowrap">
            <Text size="xs" c="dimmed" style={{ fontFamily: 'var(--rr-font-mono)' }} truncate>
              {row.fileName}
            </Text>
            {row.result === 'ok' ? (
              <Text size="xs" c="teal.7" fw={600}>
                ✓ uvoženo
              </Text>
            ) : row.result ? (
              <Text size="xs" c="red.7" fw={600}>
                ✗ {row.result.error}
              </Text>
            ) : null}
          </Group>

          {row.parseError ? (
            <Text size="xs" c="red.7">
              {row.parseError}
            </Text>
          ) : (
            <>
              <Group gap={8} wrap="nowrap" align="flex-end">
                <TextInput
                  label="Ime trase"
                  size="xs"
                  style={{ flex: 1 }}
                  value={row.name}
                  onChange={(e) => {
                    const name = e.currentTarget.value;
                    // Typing an existing course's name makes this a replacement,
                    // so show that course's colour and days straight away.
                    const match = routes.find((r) => r.name === name.trim());
                    onPatch(
                      match
                        ? { name, color: match.color, eventDayIds: match.eventDayIds }
                        : { name },
                    );
                  }}
                />
                <MultiSelect
                  label="Dnevi"
                  size="xs"
                  style={{ flex: 1.2 }}
                  placeholder="vsi dnevi"
                  // The modal itself sits above the map's layers panel, so the
                  // dropdown has to clear the modal in turn.
                  comboboxProps={{ zIndex: 1500 }}
                  data={days.map((d) => ({ value: d.id, label: d.label }))}
                  value={row.eventDayIds}
                  onChange={(v) => onPatch({ eventDayIds: v })}
                />
                <ColourPicker value={row.color} onChange={(color) => onPatch({ color })} />
                <Button size="compact-xs" variant="subtle" color="gray" onClick={onRemove}>
                  Odstrani
                </Button>
              </Group>

              {row.parsed ? (
                <Text size="xs" c="dimmed" mt={6}>
                  {formatKm(row.parsed.lengthM)} · {plT(row.parsed.pointCount)}
                  {row.parsed.lines.length > 1 ? ` · ${row.parsed.lines.length} odsekov` : ''}
                  {row.parsed.waypointCount > 0
                    ? ` · ${plT(row.parsed.waypointCount)} zanimanja v datoteki (neuporabljene)`
                    : ''}
                </Text>
              ) : null}

              {existing ? (
                <Note tone="warn">
                  Trasa »{existing.name}« že obstaja — geometrija bo zamenjana
                  {existing.source === 'placeholder'
                    ? ' (zdaj poenostavljena povezava).'
                    : ` (zdaj ${formatKm(existing.lengthM)}).`}
                </Note>
              ) : null}

              {outsideArea ? (
                <Note tone="danger">
                  Sled ne leži v območju dogodka — preveri, ali je datoteka prava in ali sta
                  širina in dolžina zamenjani.
                </Note>
              ) : null}
            </>
          )}
        </div>
      </Group>
    </div>
  );
}

function Note({ tone, children }: { tone: 'warn' | 'danger'; children: React.ReactNode }) {
  const palette =
    tone === 'warn'
      ? { bg: 'oklch(0.99 0.012 75)', border: 'oklch(0.93 0.03 75)', fg: 'oklch(0.45 0.09 75)' }
      : { bg: 'oklch(0.99 0.015 25)', border: 'oklch(0.92 0.04 25)', fg: 'oklch(0.48 0.13 25)' };
  return (
    <div
      style={{
        marginTop: 6,
        padding: '7px 9px',
        borderRadius: 7,
        background: palette.bg,
        border: `1px solid ${palette.border}`,
        color: palette.fg,
        fontSize: rem(12),
        lineHeight: 1.45,
      }}
    >
      {children}
    </div>
  );
}

function ColourPicker({ value, onChange }: { value: string; onChange: (c: string) => void }) {
  return (
    <div>
      <Text size="xs" mb={4} c="dimmed">
        Barva
      </Text>
      <Group gap={4} wrap="nowrap">
        {ROUTE_PALETTE.map((c) => (
          <button
            key={c}
            type="button"
            aria-label={`barva ${c}`}
            onClick={() => onChange(c)}
            style={{
              width: 18,
              height: 18,
              borderRadius: 5,
              background: c,
              cursor: 'pointer',
              border: value === c ? '2px solid oklch(0.3 0.02 90)' : '1px solid oklch(0.85 0.01 95)',
            }}
          />
        ))}
      </Group>
    </div>
  );
}

/**
 * Fit the preview to whatever is currently drawn. Keyed on the bbox *values*,
 * not on object identity: every keystroke in a name field rebuilds the row
 * array, and re-fitting on that would yank the map back while the coordinator
 * is panning around the track.
 */
function FitBounds({ bbox }: { bbox: Bbox | null }) {
  const map = useMap();
  const key = bbox ? bbox.join(',') : '';
  useEffect(() => {
    if (!key) return;
    const [minLng, minLat, maxLng, maxLat] = key.split(',').map(Number) as Bbox;
    map.fitBounds(
      [
        [minLat, minLng],
        [maxLat, maxLng],
      ],
      { animate: false, padding: [16, 16] },
    );
  }, [key, map]);
  return null;
}

/**
 * Draws every parsed track over the event's posts. Answering "is this the right
 * course, in the right place" at a glance is the point of the whole screen.
 */
function PreviewMap({ rows, posts }: { rows: Row[]; posts: TimelinePost[] }) {
  // Both memoised: an unstable `located` would make `bounds` a new object every
  // render, re-firing FitBounds and throwing away the coordinator's pan/zoom
  // on every keystroke in a name field.
  const located = useMemo(
    () => posts.filter((p) => p.lat !== null && p.lng !== null),
    [posts],
  );
  const bbox = useMemo(() => {
    const parts = rows.flatMap((r) => r.parsed?.lines ?? []);
    if (located.length) parts.push(located.map((p): Position => [p.lng!, p.lat!]));
    return bboxOf(parts);
  }, [rows, located]);

  return (
    <div style={{ height: 260, borderRadius: 10, overflow: 'hidden', border: '1px solid oklch(0.92 0.006 95)' }}>
      <MapContainer
        center={[46.43, 13.95]}
        zoom={10}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={false}
        attributionControl={false}
      >
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        <FitBounds bbox={bbox} />
        {located.map((p) => (
          <CircleMarker
            key={p.id}
            center={[p.lat!, p.lng!]}
            radius={3}
            pathOptions={{ color: 'oklch(0.55 0.02 95)', weight: 1, fillOpacity: 0.55 }}
          />
        ))}
        {rows.flatMap((row) =>
          (row.parsed?.lines ?? []).map((line, i) => (
            <Polyline
              key={`${row.key}-${i}`}
              positions={line.map(([lng, lat]) => [lat, lng] as [number, number])}
              pathOptions={{ color: row.color, weight: 3, opacity: 0.85 }}
            />
          )),
        )}
      </MapContainer>
    </div>
  );
}
